import { Injectable, signal, computed } from '@angular/core';
import { formatCurrency } from 'src/app/utils/currency-format';
import {
  GroupedSalesItem,
  TimeRange,
} from 'src/app/models/core/chart-interfaces';
import { EMPTY_GROUPED_SALES_ITEMS } from 'src/app/types/sale-graph-data';
import { DashboardSellerApiService } from './dashboard-seller-api.service';
import { RecentTicket } from 'src/app/models/core/recent-ticket.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardSellerService {

  // DASHBOARD KPIS
  readonly kpisLoading = signal(false);
  readonly totalSoldAmount = signal<number>(0);
  readonly totalSoldAmountFormatted = computed(() =>
    formatCurrency(this.totalSoldAmount())
  );

  readonly totalEcommerceAmount = signal<number>(0);
  readonly totalEcommerceAmountFormatted = computed(() =>
    formatCurrency(this.totalEcommerceAmount())
  );

  readonly totalDirectAmount = signal<number>(0);
  readonly totalDirectAmountFormatted = computed(() =>
    formatCurrency(this.totalDirectAmount())
  );

  // Sales Type Graph
  readonly groupedSalesDataLoading = signal(false);
  readonly groupedSalesData = signal<GroupedSalesItem[]>([]);
  public selectedSalesRange = signal<TimeRange>('daily');

  // Recent Tickets
  readonly recentTickets = signal<RecentTicket[]>([]);
  readonly loadingTickets = signal(false);

  constructor(
    private dashboardSellerApi: DashboardSellerApiService,
  ) { }

  initialLoad() {
    this.loadDashboardSellerKpisTotals();
    this.loadSalesTypeData('seller', 'daily');
    this.loadRecentTickets();
  }

  loadDataIfNeeded(): void {
    if (this.totalSoldAmount() === 0 && 
        this.totalEcommerceAmount() === 0 && 
        this.totalDirectAmount() === 0 &&
        this.groupedSalesData().length === 0 &&
        this.recentTickets().length === 0) {
      this.initialLoad();
    }
  }

  loadSalesTypeData(role: string, range: TimeRange): void {
    this.groupedSalesDataLoading.set(true);
    this.dashboardSellerApi.getDashboardSalesTypeGraphByRange(role, range).subscribe({
      next: (data) => {
        this.groupedSalesData.set(data ?? EMPTY_GROUPED_SALES_ITEMS);
      },
      error: () => {
        this.groupedSalesData.set(EMPTY_GROUPED_SALES_ITEMS);
        this.groupedSalesDataLoading.set(false);
      },
      complete: () => {
        this.groupedSalesDataLoading.set(false);
      },
    });
  }

  resetSelectedSalesRange(): void {
    this.selectedSalesRange.set('daily');
  }

  changeSalesRange(range: TimeRange): void {
    this.selectedSalesRange.set(range);
    this.loadSalesTypeData('seller', range);
  }

  loadDashboardSellerKpisTotals() {
    this.kpisLoading.set(true);

    this.dashboardSellerApi.getDashboardSellerKpis({
      role: 'seller',
      section: 'seller_dashboard'
    }).subscribe({
      next: ({ data = {} }) => {
        this.totalSoldAmount.set(data.total_active_events_amount || 0);
        this.totalDirectAmount.set(data.total_direct_sales_amount || 0);
        this.totalEcommerceAmount.set(data.total_ecommerce_sales_amount || 0);
        this.kpisLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading ticket totals', err);
        this.kpisLoading.set(false);
      }
    });
  }

  loadRecentTickets(): void {
    this.loadingTickets.set(true);

    this.dashboardSellerApi.getRecentTickets().subscribe({
      next: (response) => {
        this.recentTickets.set(response.data);
        this.loadingTickets.set(false);
      },
      error: (error) => {
        console.error('Error al cargar tickets recientes:', error);
        this.loadingTickets.set(false);
      }
    });
  }

  resetAllState(): void {
    this.totalSoldAmount.set(0);
    this.totalEcommerceAmount.set(0);
    this.totalDirectAmount.set(0);
    this.kpisLoading.set(false);

    this.groupedSalesData.set([]);
    this.groupedSalesDataLoading.set(false);
    this.selectedSalesRange.set('daily');

    this.recentTickets.set([]);
    this.loadingTickets.set(false);
  }

}
